/**
 * 🔒 Política de Privacidade — Workflow Manager (LGPD)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const PrivacidadePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>

        <h1 className="text-2xl font-bold mb-1">Política de Privacidade</h1>
        <p className="text-sm text-gray-500 mb-2">
          Em conformidade com a Lei Geral de Proteção de Dados (LGPD — Lei 13.709/2018)
        </p>
        <p className="text-sm text-gray-500 mb-8">Última atualização: 11 de março de 2026</p>

        <div className="prose prose-sm max-w-none text-gray-700 space-y-4">
          <h2 className="text-lg font-semibold mt-6">1. Controlador dos Dados</h2>
          <p>
            A empresa responsável pelo tratamento dos seus dados pessoais é a
            <strong> Workflow Manager Tecnologia Ltda</strong>, inscrita no CNPJ sob nº [completar],
            com sede em São Paulo/SP ("nós", "nosso").
          </p>

          <h2 className="text-lg font-semibold mt-6">2. Dados Coletados</h2>
          <p>Coletamos os seguintes dados:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Dados cadastrais:</strong> nome, email, empresa, cargo, telefone</li>
            <li><strong>Dados de uso:</strong> funcionalidades utilizadas, timestamps de acesso</li>
            <li><strong>Dados do processo:</strong> mapas VSM, workflows, ações 5W2H, métricas Kaizen</li>
            <li><strong>Dados técnicos:</strong> endereço IP, tipo de navegador, sistema operacional</li>
          </ul>

          <h2 className="text-lg font-semibold mt-6">3. Base Legal (Art. 7º LGPD)</h2>
          <p>O tratamento de seus dados é realizado com base em:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Execução de contrato</strong> (Art. 7º, V): prestação do serviço contratado</li>
            <li><strong>Legítimo interesse</strong> (Art. 7º, IX): melhorias de produto e segurança</li>
            <li><strong>Consentimento</strong> (Art. 7º, I): envio de comunicações de marketing</li>
          </ul>

          <h2 className="text-lg font-semibold mt-6">4. Finalidade do Tratamento</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Prestação e manutenção do serviço</li>
            <li>Autenticação e controle de acesso</li>
            <li>Faturamento e gestão de assinaturas</li>
            <li>Comunicação sobre o serviço (alertas SLA, notificações)</li>
            <li>Melhoria da plataforma e correção de bugs</li>
            <li>Cumprimento de obrigações legais</li>
          </ul>

          <h2 className="text-lg font-semibold mt-6">5. Compartilhamento</h2>
          <p>Seus dados podem ser compartilhados apenas com:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Supabase Inc.</strong> — infraestrutura de banco de dados e autenticação</li>
            <li><strong>Resend</strong> — serviço de envio de emails transacionais</li>
            <li><strong>Cloudflare</strong> — CDN e hospedagem web</li>
            <li><strong>Stripe / Gateway de pagamento</strong> — processamento de pagamentos</li>
          </ul>
          <p>
            Nenhum dado é vendido ou compartilhado com terceiros para fins publicitários.
          </p>

          <h2 className="text-lg font-semibold mt-6">6. Isolamento Multi-Tenant</h2>
          <p>
            Todas as tabelas do banco de dados são protegidas por Row Level Security (RLS).
            Seus dados são logicamente isolados e nunca acessíveis por outras organizações.
            Cada consulta é filtrada por <code>organization_id</code>.
          </p>

          <h2 className="text-lg font-semibold mt-6">7. Retenção de Dados</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Dados de conta: mantidos enquanto a conta estiver ativa</li>
            <li>Após cancelamento: dados mantidos por 30 dias para recuperação</li>
            <li>Logs de auditoria: mantidos por 2 anos para compliance</li>
            <li>Dados de pagamento: conforme obrigação fiscal (5 anos)</li>
          </ul>

          <h2 className="text-lg font-semibold mt-6">8. Direitos do Titular (Art. 18 LGPD)</h2>
          <p>Você pode exercer os seguintes direitos:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Confirmação da existência de tratamento</li>
            <li>Acesso aos dados</li>
            <li>Correção de dados incompletos ou desatualizados</li>
            <li>Anonimização ou eliminação de dados desnecessários</li>
            <li>Portabilidade dos dados (exportação em CSV/JSON)</li>
            <li>Eliminação dos dados (com as ressalvas legais)</li>
            <li>Revogação do consentimento</li>
          </ul>
          <p>
            Para exercer qualquer direito, entre em contato:
            <strong> privacidade@workflowbrasil.com</strong>
          </p>

          <h2 className="text-lg font-semibold mt-6">9. Segurança</h2>
          <p>Adotamos medidas técnicas e organizacionais para proteger seus dados:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Criptografia em trânsito (TLS 1.3) e em repouso</li>
            <li>Autenticação JWT com refresh tokens</li>
            <li>Row Level Security (RLS) em todas as tabelas</li>
            <li>Logs de auditoria para ações sensíveis</li>
            <li>Backups automáticos diários</li>
          </ul>

          <h2 className="text-lg font-semibold mt-6">10. Cookies</h2>
          <p>
            Utilizamos apenas cookies essenciais para autenticação e preferências do
            usuário (tema, idioma). Não utilizamos cookies de rastreamento ou publicidade.
          </p>

          <h2 className="text-lg font-semibold mt-6">11. Encarregado de Dados (DPO)</h2>
          <p>
            Contato do Encarregado de Proteção de Dados:
            <strong> dpo@workflowbrasil.com</strong>
          </p>

          <h2 className="text-lg font-semibold mt-6">12. Alterações</h2>
          <p>
            Esta política pode ser atualizada periodicamente. Notificaremos sobre mudanças
            significativas por email ou pela Plataforma. O uso continuado após as alterações
            constitui aceitação.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacidadePage;
